import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ActionsCell, Badge, EmptyState, IconButton, Page, PageHeader, Pagination, SearchInput, TableSkeleton, usePagination } from "../../../components/ui";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { apiError, BATCH_SIZE, getDunning, previewDunning, sendDunning } from "../../../services/dunning";
import { can } from "../../../utils/roles";
import { formatMoney, formatWhen } from "../payments-ledger/shared";
import "../payments-ledger/payments.css";
import "./dunning.css";
import MailQuotaNotice from "../emails/MailQuotaNotice";

const KIND_FILTERS = [
  { value: "", label: "All" },
  { value: "card", label: "Card" },
  { value: "mobile", label: "Mobile Money" },
];

const BRANDS = { visa: "VISA", mastercard: "Mastercard", amex: "Amex", discover: "Discover", cartes_bancaires: "CB" };
const shortDate = (date) => (date ? new Date(date).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—");

/** Card saved by the customer: brand, last 4 digits, expiry (red when expired). */
const CardCell = ({ card }) => {
  const { t } = useTranslation();
  if (!card?.last4) return <span className="ui-cell-muted">{t("Card")}</span>;
  const expired = card.expMonth && card.expYear && new Date(card.expYear, card.expMonth, 1) <= new Date();
  return (
    <>
      <span className="dunning-card">
        <span className="dunning-brand">{BRANDS[String(card.brand || "").toLowerCase()] || String(card.brand || "").toUpperCase()}</span>
        •••• {card.last4}
      </span>
      {card.expMonth && card.expYear && (
        <span className="ui-media-meta" data-expired={expired || undefined}>
          {t("Expires")} {String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}
          {expired ? ` · ${t("expired")}` : ""}
        </span>
      )}
    </>
  );
};

const StateBadge = ({ item }) => {
  const { t } = useTranslation();
  if (item.state === "grace") return <Badge color="amber" dot>{t("Grace until")} {shortDate(item.graceUntil)}</Badge>;
  if (item.state === "suspended") return <Badge color="red" dot>{t("Suspended")}</Badge>;
  return <Badge color="red" dot>{t("Payment failed")}</Badge>;
};

/**
 * Unpaid customers to remind by hand, one by one or as a group: declined card
 * renewals (grace period or suspended) and Mobile Money failures not settled
 * since. The automatic reminders keep running; a customer is reminded by hand
 * at most once every 24 hours.
 */
const Dunning = () => {
  const { t } = useTranslation();
  const canAct = can("transactions", "edit");
  const [kind, setKind] = useState("");
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null); // { item, email?, error? }
  const [confirm, setConfirm] = useState(null); // ids to remind
  const [sending, setSending] = useState(null); // { done, total }

  const load = useCallback(() => {
    getDunning({ kind, state, q: search }).then(setData);
  }, [kind, state, search]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);
  useEffect(() => setSelected([]), [kind, state, search]);

  const items = data?.items || [];
  const counts = data?.counts;
  const { page, setPage, pageCount, pageItems, total, pageSize } = usePagination(items, 25, [kind, state, search]);
  const remindable = items.filter((item) => item.canRemind);
  const pageRemindable = pageItems.filter((item) => item.canRemind);
  const allSelected = pageRemindable.length > 0 && pageRemindable.every((item) => selected.includes(item.id));
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const toggleAll = () =>
    setSelected((current) =>
      allSelected ? current.filter((id) => !pageRemindable.some((item) => item.id === id)) : [...new Set([...current, ...pageRemindable.map((item) => item.id)])]
    );

  const openPreview = (item) => {
    setPreview({ item });
    previewDunning(item.id)
      .then((email) => setPreview({ item, email }))
      .catch((error) => setPreview({ item, error: apiError(error) || t("Something went wrong") }));
  };

  /** Sends in batches of 100, with progress, then reloads the list. */
  const send = async (ids) => {
    setSending({ done: 0, total: ids.length });
    const result = { sent: 0, skipped: 0, failed: 0 };
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      try {
        const part = await sendDunning(ids.slice(i, i + BATCH_SIZE));
        result.sent += part.sent;
        result.skipped += part.skipped.length;
        result.failed += part.failed;
      } catch (error) {
        result.failed += Math.min(BATCH_SIZE, ids.length - i);
        toast.error(apiError(error) || t("Something went wrong"));
      }
      setSending({ done: Math.min(ids.length, i + BATCH_SIZE), total: ids.length });
    }
    setSending(null);
    setSelected([]);
    if (result.sent) toast.success(t("remindersSent", { count: result.sent }));
    if (result.skipped) toast.info(t("remindersSkipped", { count: result.skipped }));
    if (!result.sent && !result.skipped && !result.failed) toast.info(t("Nothing sent"));
    load();
  };

  const statCard = (value, label, filter, tone) => (
    <button
      type="button"
      className="ui-card ledger-stat"
      data-tone={tone}
      aria-pressed={state === filter}
      onClick={() => setState(state === filter ? "" : filter)}
    >
      <strong>{counts ? value : "—"}</strong>
      <span>{label}</span>
    </button>
  );

  return (
    <Page>
      <PageHeader
        title={t("Unpaid reminders")}
        subtitle={t("Customers whose payment did not go through. Remind them one by one or as a group: the automatic reminders keep running.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <div className="ledger-stats">
        {statCard(counts?.total, t("Unpaid to settle"), "")}
        {statCard(counts?.grace, t("Card · grace period"), "grace", "amber")}
        {statCard(counts?.suspended, t("Card · access suspended"), "suspended", "red")}
        {statCard(counts?.failed, t("Mobile Money · failed"), "failed", "red")}
      </div>

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <div className="ui-chips">
            {KIND_FILTERS.map((item) => (
              <button key={item.value} type="button" className="ui-chip" aria-pressed={kind === item.value} onClick={() => setKind(item.value)}>
                {t(item.label)}
              </button>
            ))}
          </div>
          <SearchInput value={search} onChange={setSearch} placeholder={t("Name, email or phone")} />
        </div>

        {canAct && (
          <div className="ledger-selection dunning-bar" role="status">
            {selected.length > 0 ? (
              <>
                <span>{t("customersSelected", { count: selected.length })}</span>
                <button type="button" className="ui-btn" disabled={Boolean(sending)} onClick={() => setConfirm(selected)}>
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  {t("Remind the selection")}
                </button>
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setSelected([])}>
                  {t("Cancel")}
                </button>
              </>
            ) : (
              <>
                <span>{t("customersToRemind", { count: remindable.length })}</span>
                <button type="button" className="ui-btn" disabled={!remindable.length || Boolean(sending)} onClick={() => setConfirm(remindable.map((item) => item.id))}>
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  {t("Remind all")} ({remindable.length})
                </button>
              </>
            )}
            {sending && (
              <span className="dunning-progress">
                {t("Sending…")} {sending.done}/{sending.total}
              </span>
            )}
          </div>
        )}

        {!data ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-checkbox-circle-line">{t("No unpaid payment to remind")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table ledger-table">
              <thead>
                <tr>
                  {canAct && (
                    <th>
                      <input type="checkbox" aria-label={t("Select all")} checked={allSelected} disabled={!pageRemindable.length} onChange={toggleAll} />
                    </th>
                  )}
                  <th>{t("Customer")}</th>
                  <th>{t("Unpaid")}</th>
                  <th>{t("Payment method")}</th>
                  <th>{t("Reason")}</th>
                  <th>{t("status")}</th>
                  <th>{t("Reminders")}</th>
                  <th aria-label={t("Actions")}></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item) => (
                  <tr key={item.id} className="ledger-row" onClick={() => openPreview(item)}>
                    {canAct && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={t("Select")}
                          disabled={!item.canRemind}
                          checked={selected.includes(item.id)}
                          onChange={() => toggle(item.id)}
                        />
                      </td>
                    )}
                    <td>
                      <span className="ui-media-title">{item.customer.name}</span>
                      <span className="ui-media-meta">{item.customer.email || t("No email")}</span>
                    </td>
                    <td>
                      <span className="ui-media-title">
                        {item.what === "exclusive" ? t("Exclusive content") : item.plan === "annual" ? t("Annual subscription") : t("Monthly subscription")}
                      </span>
                      <span className="ui-media-meta">
                        {item.what === "exclusive" && item.title ? `${item.title} · ` : ""}
                        {item.amount ? `${formatMoney(item.amount, item.currency || "USD")} · ` : ""}
                        {t("Since")} {shortDate(item.since)}
                      </span>
                    </td>
                    <td>{item.kind === "card" ? <CardCell card={item.card} /> : <span className="ui-cell-muted">{t("Mobile Money")} · {item.operator}</span>}</td>
                    <td className="ui-cell-muted">{item.reason}</td>
                    <td>
                      <StateBadge item={item} />
                    </td>
                    <td>
                      <span className="ui-media-meta">
                        {t("Automatic")} : {item.autoReminders} · {t("Manual")} : {item.manualReminders}
                      </span>
                      {item.lastManualReminderAt && (
                        <span className="ui-media-meta">
                          {t("Last")} {formatWhen(item.lastManualReminderAt)}
                        </span>
                      )}
                    </td>
                    <ActionsCell>
                      <IconButton icon="ri-eye-line" label={t("Preview the email")} onClick={() => openPreview(item)} />
                      {canAct && item.canRemind && (
                        <IconButton icon="ri-mail-send-line" label={t("Send a reminder")} onClick={() => setConfirm([item.id])} />
                      )}
                    </ActionsCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} pageCount={pageCount} total={total} pageSize={pageSize} onChange={setPage} />
        <p className="ui-hint dunning-hint">
          {t("Automatic reminders keep running: for a card, an email at the decline, at D+3, the day before the end of the 7-day grace period, then at suspension; for Mobile Money, one reminder. A customer is reminded by hand at most once every 24 hours.")}
        </p>
      </section>

      {preview && (
        <Modal show={true} onHide={() => setPreview(null)} centered size="lg" dialogClassName="ui-modal">
          <Modal.Header>
            <Modal.Title>
              {t("Reminder to")} {preview.item.customer.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {preview.error ? (
              <p className="ui-hint">{preview.error}</p>
            ) : !preview.email ? (
              <TableSkeleton rows={4} />
            ) : (
              <>
                <p className="ui-media-meta">
                  {t("To")} : {preview.email.to} · {t("Subject")} : {preview.email.subject}
                </p>
                <iframe title={t("Preview")} className="ledger-preview-frame" srcDoc={preview.email.html} sandbox="" />
                <p className="ui-hint">{t("The customer also receives a notification in the app.")}</p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setPreview(null)}>
              {t("Close")}
            </button>
            {canAct && preview.item.canRemind && (
              <button
                type="button"
                className="ui-btn"
                disabled={!preview.email || Boolean(sending)}
                onClick={() => {
                  const id = preview.item.id;
                  setPreview(null);
                  send([id]);
                }}
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                {t("Send this reminder")}
              </button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => send(confirm)}
        confirmLabel={t("Send")}
      >
        <strong>{t("remindConfirm", { count: confirm?.length || 0 })}</strong>
        <p>{t("Each customer receives an email and an app notification. Customers who settled meanwhile or were reminded by hand in the last 24 hours are skipped.")}</p>
        {confirm && <MailQuotaNotice recipients={confirm.length} />}
      </ConfirmDialog>
    </Page>
  );
};

export default Dunning;
